// components/admin/EventSearchSelect.jsx

import React, { useEffect, useRef, useState } from "react";
import { searchEvents } from "../services/AdminService";

const EventSearchSelect = ({ onSelect, placeholder = "Rechercher un événement par nom…", clearOnSelect = false }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const debounceRef = useRef(null);
    const boxRef = useRef(null);

    const imgSrc = (img) => img;

    useEffect(() => {
        if (selected) return;
        if (query.trim().length < 1) {
            setResults([]);
            setOpen(false);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await searchEvents(query.trim());
                setResults(data.events || []);
                setOpen(true);
            } catch (err) {
                console.error(err);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [query, selected]);

    useEffect(() => {
        const handler = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handlePick = (event) => {
        onSelect?.(event);
        if (clearOnSelect) {
            setSelected(null);
            setQuery("");
            setResults([]);
            setOpen(false);
            return;
        }
        setSelected(event);
        setQuery(event.nomEven || "");
        setOpen(false);
    };

    const handleReset = () => {
        setSelected(null);
        setQuery("");
        setResults([]);
        onSelect?.(null);
    };

    return (
        <div className="relative" ref={boxRef} data-testid="event-search-select">
            {/* Champ de saisie */}
            <div className="flex gap-2">
                <input
                    type="text"
                    id="event-search-input"
                    data-testid="event-search-input"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (selected) setSelected(null);
                    }}
                    placeholder={placeholder}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                {selected && (
                    <button
                        type="button"
                        data-testid="event-search-reset"
                        onClick={handleReset}
                        className="px-2 text-xs text-red-500 hover:underline"
                    >
                        changer
                    </button>
                )}
            </div>

            {/* Événement sélectionné (aperçu) */}
            {selected && (
                <div
                    className="mt-2 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-2"
                    data-testid="event-search-selected"
                >
                    <img
                        src={imgSrc(selected.imgEven)}
                        alt={selected.nomEven}
                        data-testid="event-search-selected-img"
                        className="w-12 h-12 rounded object-cover bg-gray-200"
                        onError={(e) => (e.target.style.visibility = "hidden")}
                    />
                    <div className="text-sm">
                        <div className="font-semibold text-gray-800" data-testid="event-search-selected-name">
                            {selected.nomEven}
                        </div>
                        <div className="text-xs text-gray-500" data-testid="event-search-selected-info">
                            #{selected.id} · {selected.dateEven || "date —"}
                        </div>
                    </div>
                </div>
            )}

            {/* Dropdown des résultats */}
            {open && !selected && (
                <div
                    className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto"
                    data-testid="event-search-dropdown"
                >
                    {loading && (
                        <div className="px-3 py-2 text-sm text-gray-400" data-testid="event-search-loading">
                            Recherche…
                        </div>
                    )}

                    {!loading && results.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400" data-testid="event-search-empty">
                            Aucun événement trouvé.
                        </div>
                    )}

                    {!loading &&
                        results.map((event) => (
                            <button
                                key={event.id}
                                type="button"
                                data-testid={`event-search-result-${event.id}`}
                                data-event-name={event.nomEven}
                                onClick={() => handlePick(event)}
                                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-indigo-50 text-left border-b border-gray-50 last:border-0"
                            >
                                <img
                                    src={imgSrc(event.imgEven)}
                                    alt={event.nomEven}
                                    className="w-12 h-12 rounded object-cover bg-gray-200 flex-shrink-0"
                                    onError={(e) => (e.target.style.visibility = "hidden")}
                                />
                                <div className="text-sm">
                                    <div className="font-medium text-gray-800">{event.nomEven}</div>
                                    <div className="text-xs text-gray-500">
                                        {event.dateEven || "date —"}
                                    </div>
                                </div>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

export default EventSearchSelect;